import { createTheme } from '@mui/material/styles';

/**
 * CleoLead Design System — "Warm Editorial" (Direction B, approved 2026-06-11)
 *
 * SINGLE SOURCE OF TRUTH for the landing page. The frontend app
 * (app.cleolead.com) uses IDENTICAL hex values in its light mode.
 * See DESIGN.md for the full specification.
 */

const FONT_DISPLAY = '"Fraunces", Georgia, "Times New Roman", serif';
const FONT_BODY =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const SHADOW_SOFT =
  '0 1px 2px rgba(28, 25, 23, 0.04), 0 8px 24px rgba(28, 25, 23, 0.06)';
const SHADOW_LIFT =
  '0 2px 4px rgba(28, 25, 23, 0.05), 0 16px 40px rgba(28, 25, 23, 0.08)';

/**
 * Canonical raw tokens for direct `sx` use.
 * Hex values MUST stay in sync with the app repo (explicit requirement:
 * "we need same colors in app and landing").
 */
export const tokens = {
  // Surfaces
  bgDefault: '#FAF7F1', // warm cream — page background
  bgPaper: '#FFFFFF', // cards, surfaces
  bandAlt: '#EDE7DC', // alternating section bands, stat strips

  // Ink (text + primary button fill)
  ink: '#1C1917',
  inkHover: '#2D2823', // primary button hover
  textSecondary: '#6B6259',

  // Copper (secondary/accent: links, overlines, key numbers, hovers)
  copper: '#C75B39',
  copperLight: '#D97757',
  copperDark: '#A8492D',
  copperTint: 'rgba(199, 91, 57, 0.10)', // chips/active states (0.08–0.12 range)
  copperTintStrong: 'rgba(199, 91, 57, 0.12)',

  // Sage (success / 'booked' / AI-acting states)
  sage: '#2F5D50',
  sageLight: '#4A7A6B',
  sageTint: 'rgba(47, 93, 80, 0.10)', // booked/success chips

  // Status
  warning: '#B45309', // amber, rare
  error: '#B3261E',

  // Hairlines
  border: '#E5DDD0', // 1px borders, dividers

  // Shape — ONLY these two radii
  radiusControl: 10,
  radiusCard: 16,

  // Elevation
  shadowSoft: SHADOW_SOFT,
  shadowLift: SHADOW_LIFT,

  // Motion
  motion: {
    micro: 150, // ms — hovers, taps
    transition: 250, // ms — standard transitions
    reveal: 400, // ms — scroll reveals / entrances
    stagger: 70, // ms — sibling stagger
    easeEntrance: 'cubic-bezier(0.16, 1, 0.3, 1)', // expo-out, for entrances
    easeHover: 'ease', // for hovers
  },

  // Type
  fontDisplay: FONT_DISPLAY,
  fontBody: FONT_BODY,
} as const;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.ink,
      dark: tokens.inkHover,
      light: '#3D3833',
      contrastText: '#FAF7F1',
    },
    secondary: {
      main: tokens.copper,
      light: tokens.copperLight,
      dark: tokens.copperDark,
      contrastText: '#FFFFFF',
    },
    success: {
      main: tokens.sage,
      light: tokens.sageLight,
      dark: '#244A40',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: tokens.warning,
    },
    error: {
      main: tokens.error,
    },
    background: {
      default: tokens.bgDefault,
      paper: tokens.bgPaper,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.textSecondary,
    },
    divider: tokens.border,
  },
  typography: {
    fontFamily: FONT_BODY,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontSize: '3.5rem',
      fontWeight: 600,
      lineHeight: 1.08,
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '2.25rem',
      },
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.12,
      letterSpacing: '-0.01em',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontSize: '1.6rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontFamily: FONT_BODY,
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h5: {
      fontFamily: FONT_BODY,
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h6: {
      fontFamily: FONT_BODY,
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1.05rem',
      lineHeight: 1.7,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: '1.05rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    subtitle2: {
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.8rem',
      lineHeight: 1.5,
      color: tokens.textSecondary,
    },
    overline: {
      fontFamily: FONT_BODY,
      fontSize: '0.78rem',
      fontWeight: 600,
      letterSpacing: '0.14em',
      textTransform: 'uppercase' as const,
      color: tokens.copper,
      lineHeight: 1.6,
    },
    button: {
      fontFamily: FONT_BODY,
      fontWeight: 600,
      textTransform: 'none' as const,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bgDefault,
          color: tokens.ink,
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        '::selection': {
          backgroundColor: tokens.copperTintStrong,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontFamily: FONT_BODY,
          fontWeight: 600,
          borderRadius: tokens.radiusControl,
          padding: '11px 24px',
          fontSize: '0.95rem',
          lineHeight: 1.2,
          transition: `background-color ${tokens.motion.micro}ms ease, color ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease, box-shadow ${tokens.motion.micro}ms ease, transform ${tokens.motion.micro}ms ease`,
        },
        contained: {
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: tokens.ink,
          color: '#FAF7F1',
          '&:hover': {
            backgroundColor: tokens.inkHover,
            boxShadow: '0 8px 24px rgba(199, 91, 57, 0.25)',
          },
          '&:active': {
            backgroundColor: tokens.inkHover,
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: tokens.copper,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: tokens.copperDark,
            boxShadow: '0 8px 24px rgba(199, 91, 57, 0.25)',
          },
        },
        outlined: {
          backgroundColor: '#FFFFFF',
          borderColor: tokens.border,
          color: tokens.ink,
          '&:hover': {
            backgroundColor: '#FFFFFF',
            borderColor: tokens.copper,
            color: tokens.copper,
          },
        },
        text: {
          color: tokens.ink,
          '&:hover': {
            backgroundColor: 'transparent',
            color: tokens.copper,
          },
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.bgPaper,
        },
        rounded: {
          borderRadius: tokens.radiusCard,
        },
        outlined: {
          borderColor: tokens.border,
        },
        elevation1: {
          boxShadow: SHADOW_SOFT,
          border: `1px solid ${tokens.border}`,
        },
        elevation2: {
          boxShadow: SHADOW_SOFT,
          border: `1px solid ${tokens.border}`,
        },
        elevation3: {
          boxShadow: SHADOW_LIFT,
          border: `1px solid ${tokens.border}`,
        },
        elevation4: {
          boxShadow: SHADOW_LIFT,
          border: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.bgPaper,
          borderRadius: tokens.radiusCard,
          border: `1px solid ${tokens.border}`,
          boxShadow: SHADOW_SOFT,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(250, 247, 241, 0.88)',
          color: tokens.ink,
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.border}`,
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_BODY,
          fontWeight: 500,
          borderRadius: 8,
        },
        outlined: {
          borderColor: tokens.border,
          backgroundColor: tokens.bgDefault,
        },
        colorSecondary: {
          backgroundColor: tokens.copperTint,
          color: tokens.copperDark,
          '&.MuiChip-outlined': {
            borderColor: 'rgba(199, 91, 57, 0.35)',
            backgroundColor: tokens.copperTint,
          },
        },
        colorSuccess: {
          backgroundColor: tokens.sageTint,
          color: tokens.sage,
          '&.MuiChip-outlined': {
            borderColor: 'rgba(47, 93, 80, 0.35)',
            backgroundColor: tokens.sageTint,
          },
        },
        labelSmall: {
          fontWeight: 600,
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.bgPaper,
          backgroundImage: 'none',
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radiusCard,
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
            boxShadow: SHADOW_SOFT,
          },
          '&:first-of-type, &:last-of-type': {
            borderRadius: tokens.radiusCard,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '& .MuiAccordionSummary-content': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          paddingBottom: 24,
          color: tokens.textSecondary,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover' as const,
      },
      styleOverrides: {
        root: {
          color: tokens.copper,
          textDecorationColor: 'rgba(199, 91, 57, 0.4)',
          transition: `color ${tokens.motion.micro}ms ease`,
          '&:hover': {
            color: tokens.copperDark,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.border,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.ink,
          color: '#FAF7F1',
          fontSize: '0.78rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 12px',
        },
        arrow: {
          color: tokens.ink,
        },
      },
    },
  },
});

// Soft elevation scale: levels 1-2 use the soft card shadow, 3-6 the lift shadow.
theme.shadows[1] = SHADOW_SOFT;
theme.shadows[2] = SHADOW_SOFT;
theme.shadows[3] = SHADOW_LIFT;
theme.shadows[4] = SHADOW_LIFT;
theme.shadows[5] = SHADOW_LIFT;
theme.shadows[6] = SHADOW_LIFT;

export default theme;
